import React, { Component, Fragment, lazy, Suspense } from 'react';
import './App.css';
import { AxlLoading } from 'axl-reactjs-ui';
import { Provider, Observer } from 'mobx-react';
import { ToastContainer } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import { ThemeProvider } from '@material-ui/core/styles';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'queries/queryClient';

import Header from 'containers/Header';
import MainMenuWrapper from './MainMenuWrapper';
import Firebase, { FirebaseContext } from './components/Firebase';
import AxlLeftSideMenu from './components/AxlLeftSideMenu';
import RequestErrorHandlerManager from './containers/RequestHandlerManager';
import ProtectedRoute from './components/ProtectedRoute';

const ToastScreen = lazy(() => import('screens/Toast'));
const DispatchScreen = lazy(() => import('screens/Dispatch'));
const AssignmentDetailScreen = lazy(() => import('screens/AssignmentDetail'));
const SearchScreen = lazy(() => import('screens/Search'));
const ScheduleScreen = lazy(() => import('screens/Schedule'));
const DriverRegistrationScreen = lazy(() => import('screens/DriverRegistration'));
const DriverListScreen = lazy(() => import('screens/Drivers'));
const DriverPoolScreen = lazy(() => import('screens/DriverPool'));
const DriverIncentiveScreen = lazy(() => import('screens/DriverIncentive'));
const DriverCrewScreen = lazy(() => import('screens/DriverCrew'));
const DashboardScreen = lazy(() => import('screens/Dashboard'));
const DriverPickupScreen = lazy(() => import('screens/DriverPickupTime'));
const CallCenterScreen = lazy(() => import('screens/CallCenter'));
const DriverAnnouncementScreen = lazy(() => import('screens/DriverAnnouncement'));
const DriverSuspensionScreen = lazy(() => import('screens/DriverSuspension'));
const MessengerScreen = lazy(() => import('screens/Messenger'));
const TicketBookingScreen = lazy(() => import('screens/TicketBooking'));
const TicketBookingSessionScreen = lazy(() => import('screens/TicketBookingSession'));
const RegionSettingsScreen = lazy(() => import('screens/RegionSettings'));
const AddRedelivery = lazy(() => import('screens/AddRedelivery'));
const ShipmentScreen = lazy(() => import('screens/Shipment'));
const DriverDetailScreen = lazy(() => import('screens/DriverDetailScreen'));
const Frankenroute = lazy(() => import('screens/Frankenroute'));
const DriverRatingConfig = lazy(() => import('./containers/DriverRatingConfig'));
const CallCenterAbandondedScreen = lazy(() => import('./containers/CallCenterAbandoned'));
const Linehaul = lazy(() => import('screens/Linehaul'));
const LinehaulDashboard = lazy(() => import('screens/LHDashboard'));
const LinehaulPlaceholder = lazy(() => import('screens/LinehaulPlaceholder'));

import { stores } from './stores';
import { lightTheme } from './themes';
import 'maplibre-gl/dist/maplibre-gl.css';
import 'react-toastify/dist/ReactToastify.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'flatpickr/dist/themes/material_green.css';
import SubMenu from './SubMenu';

const LoadingIndicator = () => {
  return (
    <div className="loading">
      <AxlLoading color="#FFF" size={75} thin={1} />
    </div>
  );
};
class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isActive: false,
      firebase: new Firebase(),
      loading: false,
    };
  }

  async componentDidMount() {
    // Add translate control logic here
    const htmlElement = document.documentElement;
    const translateValue = process.env.REACT_APP_YES_NO_BROWSER_TRANSLATE || 'yes';
    htmlElement.setAttribute('translate', translateValue);
    const { firebase } = this.state;
    this.setState({ loading: true });
    await stores.userStore.getUser();
    await stores.clientStore.getActiveClients();
    await stores.permissionStore.getPermissionSetting();
    await firebase.initFirebaseMessage();

    if (datadogRum && stores.userStore && stores.userStore.user) {
      datadogRum.setUser({
        id: stores.userStore.user.id,
        name: stores.userStore.user.name,
        scopes: stores.userStore.user.scopes,
      });
    }
    this.setState({ loading: false });
  }

  toogleMenuSlider = () => {
    this.setState({ isActive: !this.state.isActive });
  };

  closeMenuSlider = () => {
    this.setState({ isActive: false });
  };

  logout() {
    stores?.sensitiveInfoStore?.resetData();
    window.location.href = `${process.env.REACT_APP_OAUTH_URL}/login?logout=true&next=${encodeURI(window.location.href)}`;
  }

  render() {
    const { isActive, firebase, loading } = this.state;
    const { isFetchedUser, user } = stores.userStore;
    const DEFINED = {
      UNKNOWN_DRIVER: 1,
      UNKNOWN_RECIPIENT: 2,
      DRIVER: 8,
      RECIPIENT: 9,
    };
    const urlParams = new URLSearchParams(window.location.search);
    const callArgs = callArgsHandle(urlParams.get('call_args')) || [9];

    function callArgsHandle(value) {
      if (!value) return null;

      return value.split('|').map((a) => parseInt(a));
    }

    if (loading || !isFetchedUser || !user) return <LoadingIndicator />;

    return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={lightTheme}>
          <Provider {...stores}>
            <FirebaseContext.Provider value={firebase}>
              <Suspense fallback={<LoadingIndicator />}>
                <Router>
                  <div className="App">
                    <Observer
                      render={() =>
                        stores.lnfStore.fullscreen ? (
                          <Fragment />
                        ) : (
                          <Fragment>
                            <Header onClick={this.toogleMenuSlider} logout={this.logout} />
                            <SubMenu />
                          </Fragment>
                        )
                      }
                    />
                    <MainMenuWrapper stores={stores} />
                    <Observer
                      render={() => (
                        <div
                          onClick={() => stores.lnfStore.toggleFullScreen()}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '16px',
                            position: 'absolute',
                            top: stores.lnfStore.fullscreen ? '2px' : '60px',
                            right: '8px',
                            opacity: stores.lnfStore.fullscreen ? 0.2 : 0.05,
                            backgroundColor: 'white',
                            zIndex: 1000,
                            cursor: 'pointer',
                          }}
                        >
                          <i className={'fa fa-arrows'} style={{ fontSize: '16px', padding: '8px' }} />
                        </div>
                      )}
                    />
                    <div className="main-content">
                      <Switch>
                        <ProtectedRoute path="/linehauls/summary" component={Linehaul} />
                        <ProtectedRoute path="/linehauls/report" component={LinehaulPlaceholder} />
                        <ProtectedRoute path="/linehauls/overview" component={LinehaulPlaceholder} />
                        <ProtectedRoute path="/linehauls" component={LinehaulDashboard} />
                        <ProtectedRoute path="/dashboard" component={DashboardScreen} />
                        <ProtectedRoute path="/driver-registration" component={DriverRegistrationScreen} />
                        <ProtectedRoute path="/drivers/:id" component={DriverDetailScreen} />
                        <ProtectedRoute path="/drivers" component={DriverListScreen} />
                        <ProtectedRoute path="/driver-crews" component={DriverCrewScreen} />
                        <ProtectedRoute path="/driver-pools" component={DriverPoolScreen} />
                        <ProtectedRoute path="/driver-incentives" component={DriverIncentiveScreen} />
                        <ProtectedRoute path="/driver-announcements" component={DriverAnnouncementScreen} />
                        <ProtectedRoute path="/driver-probations" component={DriverSuspensionScreen} />
                        <ProtectedRoute path="/toast" component={ToastScreen} />
                        <ProtectedRoute path="/search" component={SearchScreen} />
                        <ProtectedRoute path="/schedule" component={ScheduleScreen} />
                        <ProtectedRoute path="/assignments/:assignmentId" component={AssignmentDetailScreen} />
                        <ProtectedRoute path="/assignments" component={DashboardScreen} />
                        <ProtectedRoute path="/routes/:day/:region/:client/:assignmentId/stops/:stopId" component={DispatchScreen} />
                        <ProtectedRoute path="/routes/:day/:region/:client/:assignmentId" component={DispatchScreen} />
                        <ProtectedRoute path="/routes/:day/:region/:client" component={DispatchScreen} />
                        <ProtectedRoute path="/routes/:day/:region" component={DispatchScreen} />
                        <ProtectedRoute path="/routes/:day" component={DispatchScreen} />
                        <ProtectedRoute path="/routes" component={DispatchScreen} />
                        <ProtectedRoute path="/driver-pickup" component={DriverPickupScreen} />
                        <ProtectedRoute path="/messenger/:id" component={MessengerScreen} />
                        <ProtectedRoute path="/messenger" component={MessengerScreen} />
                        <Route
                          path="/call-center/results"
                          render={() => {
                            if ([DEFINED.UNKNOWN_RECIPIENT, DEFINED.RECIPIENT].indexOf(callArgs[0]) !== -1) {
                              return <RedirectCallCenter />;
                            } else {
                              return <CallCenterScreen />;
                            }
                          }}
                        />
                        <ProtectedRoute path="/ticket-booking" exact={true} component={TicketBookingScreen} />
                        <ProtectedRoute path="/ticket-booking/:id" component={TicketBookingSessionScreen} />
                        <ProtectedRoute path="/region-settings" component={RegionSettingsScreen} />
                        <ProtectedRoute path="/abandoned-calls" component={CallCenterAbandondedScreen} />
                        <Route
                          path="/redelivery/multi"
                          render={() => {
                            if (stores.userStore.isSuperAdmin) {
                              return <AddRedelivery />;
                            }

                            window.location.href = process.env.REACT_APP_REDELIVERY_URL;
                            return null;
                          }}
                        />
                        <ProtectedRoute path="/shipments/:id" component={ShipmentScreen} />
                        <ProtectedRoute path={['/frankenroute/:id', '/frankenroute']} component={Frankenroute} />
                        <ProtectedRoute path="/driver-rating-config" component={DriverRatingConfig} />
                        <Route path="/" component={DispatchScreen} />
                      </Switch>
                    </div>
                    <AxlLeftSideMenu isActive={isActive} onClose={this.closeMenuSlider} />
                  </div>
                </Router>
              </Suspense>
              <ToastContainer enableMultiContainer containerId="main" />
              <RequestErrorHandlerManager />
            </FirebaseContext.Provider>
          </Provider>
        </ThemeProvider>
      </QueryClientProvider>
    );
  }
}

export default App;

class RedirectCallCenter extends React.PureComponent {
  render() {
    window.location.href = `${process.env.REACT_APP_SUPPORT_URL}/call-center/results${window.location.search}`;
    return <div>Redirecting...</div>;
  }
}
